import { RULES_2026 as R, D } from "@ftr/tax-core";
import { Link } from "../lib/router.tsx";
import { daysUntil, pct, shortDate, todayIso } from "../lib/format.ts";

export interface KeyFigureLinks {
  quarterly: string;
  prop: string;
  sixtyForty: string;
  guides: string;
}

/**
 * Four facts a futures or prop trader acts on, each leading to the page that works it out. They sit in the bottom of
 * the hero's example card. The figures come from the 2026 rules file, so they change only when the rules do. Where
 * each one leads is read out to screen readers; on screen an arrow appears on hover.
 */
export function KeyFigures({ links }: { links: KeyFigureLinks }) {
  const today = todayIso();
  const next = R.estimatedTax.dueDates.find((d) => d >= today);
  const days = next ? daysUntil(next, today) : null;
  const seRate = new D(R.seTax.socialSecurityRate).plus(R.seTax.medicareRate);
  const longTerm = new D(R.section1256.longTermShare);
  const items = [
    ...(next
      ? [
          {
            key: "due",
            label: "Next estimated payment due",
            value: `${shortDate(next)} · ${days === 0 ? "today" : `${days} day${days === 1 ? "" : "s"}`}`,
            go: "Work out my payment",
            href: links.quarterly,
          },
        ]
      : []),
    { key: "prop", label: "Prop payouts", value: `${pct(seRate)} self-employment tax`, go: "See what to set aside", href: links.prop },
    { key: "1256", label: "Futures in your own account", value: `${pct(longTerm, 0)} taxed as long-term`, go: "See what 60/40 saves", href: links.sixtyForty },
    { key: "forms", label: "Where it goes on your return", value: "Schedule C and Form 6781", go: "Read the filing guides", href: links.guides },
  ];
  return (
    <ul className="stats" aria-label="Key 2026 tax facts">
      {items.map((it) => (
        <li key={it.key} className={it.key === "due" ? "stat--due" : it.key === "1256" ? "stat--gain" : undefined}>
          <Link href={it.href} className="stat-link">
            <span className="stat-label">{it.label}</span>
            <span className="stat-value">{it.value}</span>
            <span className="sr-only">{it.go}</span>
            <span className="stat-go" aria-hidden="true">
              →
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
